<template>
  <v-row class="ma-0 h-100">
    <split-view>
      <template #left>
        <v-list
          v-model:selected="selectedStep"
          density="compact"
          bg-color="transparent"
          mandatory
          class="pa-0"
        >
          <v-list-item
            v-for="step in steps"
            :key="step.value"
            :value="step.value"
            class="step-item"
            :ripple="false"
          >
            <template #prepend>
              <v-icon
                :icon="step.doneIcon"
                :color="step.done ? 'green' : undefined"
                size="small"
                class="mr-2"
              />
            </template>
            <v-list-item-title class="text-body-2">
              {{ step.label }}
            </v-list-item-title>
            <template #append>
              <v-icon
                v-if="step.statusIcon"
                :icon="step.statusIcon"
                :color="step.statusColor"
                size="x-small"
              />
            </template>
          </v-list-item>
        </v-list>
      </template>

      <template #right>
        <!-- Loading overlay -->
        <div v-if="isLoading" class="d-flex justify-center align-center py-10">
          <v-progress-circular color="primary" size="36" indeterminate class="mr-3" />
          <span>{{ loadingText }}</span>
        </div>

        <!-- Step 1: Validate Domain Model -->
        <div
          v-else-if="activeStep === 'validate'"
          class="d-flex flex-column pa-4"
          style="max-width: 800px;"
        >
          <div class="mb-2">
            <div class="d-flex align-center ga-2">
              <p class="font-weight-bold">1. Validate Domain Model</p>
              <v-chip
                v-if="hasValidationIssues"
                :color="majorIssues.length > 0 ? 'error' : (minorIssues.length > 0 ? 'warning' : 'info')"
                size="small"
                variant="flat"
                class="ml-2"
              >
                {{ validationSummary }}
              </v-chip>
              <v-chip
                v-else
                color="success"
                size="small"
                variant="tonal"
                class="ml-2"
              >
                <v-icon start size="small">mdi-check</v-icon>
                Valid
              </v-chip>
            </div>
            <div v-if="hasValidationIssues" class="mt-3 ml-8">
              <div v-if="majorIssues.length > 0" class="mb-3">
                <div class="text-subtitle-2 text-error mb-1 d-flex align-center ga-1">
                  <v-icon size="small" color="error">mdi-alert-circle</v-icon>
                  Major Issues
                </div>
                <v-list density="compact" class="validation-list bg-transparent pa-0">
                  <v-list-item
                    v-for="issue in majorIssues"
                    :key="issue.id + issue.code"
                    class="validation-item px-2 rounded"
                    @click="navigateToIssue(issue)"
                  >
                    <template #prepend>
                      <v-icon :icon="getValidationIcon(issue.type)" size="small" color="error" />
                    </template>
                    <v-list-item-title class="text-body-2">
                      {{ issue.message }}
                    </v-list-item-title>
                    <template #append>
                      <v-icon size="small" color="grey">mdi-arrow-right</v-icon>
                    </template>
                  </v-list-item>
                </v-list>
              </div>
              <div v-if="minorIssues.length > 0" class="mb-3">
                <div class="text-subtitle-2 text-warning mb-1 d-flex align-center ga-1">
                  <v-icon size="small" color="warning">mdi-alert</v-icon>
                  Minor Issues
                </div>
                <v-list density="compact" class="validation-list bg-transparent pa-0">
                  <v-list-item
                    v-for="issue in minorIssues"
                    :key="issue.id + issue.code + (issue.field || '')"
                    class="validation-item px-2 rounded"
                    @click="navigateToIssue(issue)"
                  >
                    <template #prepend>
                      <v-icon :icon="getValidationIcon(issue.type)" size="small" color="warning" />
                    </template>
                    <v-list-item-title class="text-body-2">
                      {{ issue.message }}
                    </v-list-item-title>
                    <template #append>
                      <v-icon size="small" color="grey">mdi-arrow-right</v-icon>
                    </template>
                  </v-list-item>
                </v-list>
              </div>
              <div v-if="noticeIssues.length > 0">
                <div class="text-subtitle-2 text-info mb-1 d-flex align-center ga-1">
                  <v-icon size="small" color="info">mdi-information-outline</v-icon>
                  Notices
                </div>
                <v-list density="compact" class="validation-list bg-transparent pa-0">
                  <v-list-item
                    v-for="issue in noticeIssues"
                    :key="issue.id + issue.code + (issue.field || '')"
                    class="validation-item px-2 rounded"
                    @click="navigateToIssue(issue)"
                  >
                    <template #prepend>
                      <v-icon :icon="getValidationIcon(issue.type)" size="small" color="info" />
                    </template>
                    <v-list-item-title class="text-body-2">
                      {{ issue.message }}
                    </v-list-item-title>
                    <template #append>
                      <v-icon size="small" color="grey">mdi-arrow-right</v-icon>
                    </template>
                  </v-list-item>
                </v-list>
              </div>
            </div>
          </div>
        </div>

        <!-- Step 2: Review Specification -->
        <div
          v-else-if="activeStep === 'review'"
          class="d-flex flex-column h-100"
          style="position: relative;"
        >
          <div class="d-flex align-center ga-3 pa-4 pb-0">
            <p class="font-weight-bold">2. Review Specification</p>
            <toggle
              mandatory
              :value="specViewFormat"
              :options="specFormatOptions"
              @update:modelValue="specViewFormat = $event"
            />
            <v-btn
              v-if="hasPendingChanges && !specApproved"
              class="approve-btn"
              :disabled="store.state.workflow.isViewOnlyEnabled"
              @click="specApproved = true"
            >
              <v-icon start size="small" color="error">mdi-alert-circle</v-icon>
              Approve Changes
            </v-btn>
            <v-chip
              v-else-if="specApproved"
              color="success"
              size="small"
              variant="tonal"
            >
              <v-icon start size="small">mdi-check</v-icon>
              Approved
            </v-chip>
            <v-chip
              v-else-if="isSpecSynced"
              color="success"
              size="small"
              variant="tonal"
            >
              <v-icon start size="small">mdi-check</v-icon>
              Up to date
            </v-chip>
          </div>

          <div class="flex-grow-1 mt-3">
            <code-diff-viewer
              v-if="githubFileContent !== null && githubFileContent !== currentExportJson"
              :key="'spec-diff-' + specViewFormat + (githubFileContent || '').length"
              :original="displayGithubContent"
              :modelValue="displaySpec"
              :isDirty="false"
              :readOnly="true"
              :language="displayLanguage"
            />
            <code-editor
              v-else
              :modelValue="displaySpec"
              :isDirty="false"
              :readOnly="true"
              :language="displayLanguage"
            />
          </div>
        </div>

        <!-- Step 3: Connect to GitHub -->
        <div
          v-else-if="activeStep === 'connect'"
          class="d-flex flex-column pa-4"
          style="max-width: 800px;"
        >
          <div class="d-flex align-center ga-2 mb-3 pt-1">
            <p class="font-weight-bold">3. Connect to GitHub</p>
          </div>

          <div v-if="!githubRepo">
            <connect-github
              v-model:selectedAccount="selectedAccount"
              :selectedRepo="selectedRepo"
              :selectedBranch="githubBranch"
              @update:selectedRepo="(repo) => selectedRepo = repo"
              @update:selectedBranch="(branch) => pendingBranch = branch"
              @onRepoCreated="onRepoCreated"
              @onLinkRepo="linkRepo"
            />
          </div>

          <div v-else>
            <div class="d-flex align-center ga-2 mb-2">
              <v-icon>mdi-github</v-icon>
              <v-tooltip location="bottom" open-delay="500">
                <template v-slot:activator="{ props }">
                  <span v-bind="props" class="font-weight-bold repo-name-ellipsis">
                    {{ githubRepo }}
                  </span>
                </template>
                <span>{{ githubRepo }}</span>
              </v-tooltip>
              <v-btn
                size="x-small"
                variant="text"
                @click="unlinkRepo"
                :disabled="store.state.isWorkflowDisabled"
              >
                <v-icon class="mr-1">mdi-link-off</v-icon>
                Unlink
              </v-btn>
            </div>
            <v-select
              :model-value="githubBranch"
              :items="branches"
              label="Branch"
              item-title="name"
              item-value="name"
              variant="outlined"
              density="compact"
              style="max-width: 420px"
              @update:model-value="handleBranchChange"
            />
          </div>
        </div>

        <!-- Step 4: Push Specification -->
        <div
          v-else-if="activeStep === 'push'"
          class="d-flex flex-column pa-4"
          style="max-width: 800px;"
        >
          <div class="d-flex align-center ga-2 mb-3 pt-1">
            <p class="font-weight-bold">4. Push Specification to GitHub</p>
          </div>

          <v-text-field
            v-model="specFilePath"
            label="File path in repository"
            variant="outlined"
            density="compact"
            style="max-width: 420px"
            class="mb-3"
            hint="Path where the specification will be saved (e.g. specification.json, docs/spec.json)"
            persistent-hint
            @blur="saveFilePath"
          />

          <div class="d-flex align-center ga-3">
            <v-tooltip location="top">
              <template v-slot:activator="{ props }">
                <div v-bind="props">
                  <v-btn
                    @click="pushSpecToGithub"
                    :disabled="!canPush"
                    :loading="isPushing"
                  >
                    <v-icon start>mdi-cloud-upload</v-icon>
                    Push Specification
                  </v-btn>
                </div>
              </template>
              <span>{{ pushTooltipText }}</span>
            </v-tooltip>

            <v-chip
              v-if="githubRepo && isSpecSynced"
              color="success"
              size="small"
              variant="tonal"
            >
              <v-icon start size="small">mdi-check</v-icon>
              Up to date
            </v-chip>
          </div>
        </div>
      </template>
    </split-view>
  </v-row>
</template>

<script setup>
import { computed, ref, watch, onMounted } from 'vue'
import { useStore } from 'vuex'
import SplitView from '@/components/SplitView.vue'
import ConnectGithub from '@/components/ConnectGithub.vue'
import Toggle from '@/components/Toggle.vue'
import CodeDiffViewer from '@/components/CodeDiffViewer.vue'
import CodeEditor from '@/components/CodeEditor.vue'
import { validateDomainModel, getValidationIcon, Severity } from '@/utils/domainModelValidator'
import { buildWorkflowSpec } from '@/utils/workflowExport'
import gitHubApi from '@/api/gitHubApi'
import yaml from 'js-yaml'

const props = defineProps({
  boundedContext: {
    type: Object,
    required: true
  }
})

const emit = defineEmits(['navigateToDomainView'])

const store = useStore()
const workflow = computed(() => store.state.workflow.workflow)

// Step navigation
const selectedStep = ref(['validate'])
const activeStep = computed(() => selectedStep.value[0] || 'validate')

// Internal state
const isLoading = ref(false)
const loadingText = ref('')
const isPushing = ref(false)
const specApproved = ref(false)
const specViewFormat = ref('json')
const specFormatOptions = [
  { value: 'json', label: ' JSON ' },
  { value: 'yaml', label: ' YAML ' }
]

// GitHub state — shared with Code View via bounded context fields
const githubAccountLogin = ref(props.boundedContext.githubAccountLogin || '')
const githubRepo = ref(props.boundedContext.githubRepo || '')
const githubBranch = ref(props.boundedContext.githubBranch || null)
const specFilePath = ref(props.boundedContext.specFilePath || 'qlerify-domain-model.json')
const selectedRepo = ref(null)
const selectedAccount = ref(null)
const pendingBranch = ref(null)
const branches = ref([])
const githubFileContent = ref(null)

// Computed
const cardTypesMap = computed(() => {
  const cardTypes = store.state.cardTypes?.cardTypes || []
  return Object.fromEntries(cardTypes.map((ct) => [ct.id, ct]))
})

const currentExportJson = computed(() => {
  if (!workflow.value || !props.boundedContext) {
    return ''
  }
  return buildWorkflowSpec(workflow.value, props.boundedContext, cardTypesMap.value)
})

const toYaml = (jsonStr) => {
  try {
    return yaml.dump(JSON.parse(jsonStr), { lineWidth: -1, noRefs: true })
  } catch {
    return jsonStr
  }
}

const displaySpec = computed(() => {
  if (specViewFormat.value === 'yaml') {
    return toYaml(currentExportJson.value)
  }
  return currentExportJson.value
})

const displayGithubContent = computed(() => {
  if (githubFileContent.value === null) {
    return null
  }
  if (specViewFormat.value === 'yaml') {
    return toYaml(githubFileContent.value)
  }
  return githubFileContent.value
})

const displayLanguage = computed(() => specViewFormat.value === 'yaml' ? 'yaml' : 'json')

const validationResults = computed(() => {
  return validateDomainModel(workflow.value, props.boundedContext)
})

const allIssues = computed(() => validationResults.value.issues || [])
const majorIssues = computed(() => allIssues.value.filter((i) => i.severity === Severity.MAJOR))
const minorIssues = computed(() => allIssues.value.filter((i) => i.severity === Severity.MINOR))
const noticeIssues = computed(() => allIssues.value.filter((i) => i.severity === Severity.NOTICE))
const isValidationValid = computed(() => majorIssues.value.length === 0)
const hasValidationIssues = computed(() => allIssues.value.length > 0)

const validationSummary = computed(() => {
  const parts = []
  if (majorIssues.value.length > 0) {
    parts.push(`${majorIssues.value.length} major`)
  }
  if (minorIssues.value.length > 0) {
    parts.push(`${minorIssues.value.length} minor`)
  }
  if (noticeIssues.value.length > 0) {
    parts.push(`${noticeIssues.value.length} ${noticeIssues.value.length === 1 ? 'notice' : 'notices'}`)
  }
  return parts.join(' \u2022 ')
})

const isSpecSynced = computed(() => {
  if (!githubRepo.value || githubFileContent.value === null) {
    return false
  }
  return githubFileContent.value === currentExportJson.value
})

const hasPendingChanges = computed(() => {
  if (githubFileContent.value === null) {
    return !!currentExportJson.value
  }
  return githubFileContent.value !== currentExportJson.value
})

const canPush = computed(() => {
  return !!githubRepo.value &&
    !!specFilePath.value &&
    !!currentExportJson.value &&
    !isSpecSynced.value &&
    specApproved.value &&
    !store.state.isWorkflowDisabled
})

const pushTooltipText = computed(() => {
  if (!githubRepo.value) {
    return 'Connect a GitHub repository first.'
  }
  if (!specApproved.value) {
    return 'Review and approve the specification first.'
  }
  if (!specFilePath.value) {
    return 'Specify a file path for the specification.'
  }
  if (isSpecSynced.value) {
    return 'Specification is up to date with the repository.'
  }
  return `Push the specification to ${specFilePath.value}`
})

const getValidateStatusIcon = (hasIssues, majorCount) => {
  if (!hasIssues) {
    return null
  }
  if (majorCount > 0) {
    return 'mdi-alert-circle'
  }
  return 'mdi-alert'
}

const getValidateStatusColor = (majorCount, minorCount) => {
  if (majorCount > 0) {
    return 'error'
  }
  if (minorCount > 0) {
    return 'warning'
  }
  return 'info'
}

// Left sidebar step definitions
const steps = computed(() => [
  {
    value: 'validate',
    label: '1. Validate',
    done: isValidationValid.value,
    doneIcon: isValidationValid.value ? 'mdi-checkbox-marked-outline' : 'mdi-checkbox-blank-outline',
    statusIcon: getValidateStatusIcon(hasValidationIssues.value, majorIssues.value.length),
    statusColor: getValidateStatusColor(majorIssues.value.length, minorIssues.value.length)
  },
  {
    value: 'review',
    label: '2. Review Spec',
    done: specApproved.value || isSpecSynced.value,
    doneIcon: specApproved.value || isSpecSynced.value ? 'mdi-checkbox-marked-outline' : 'mdi-checkbox-blank-outline',
    statusIcon: hasPendingChanges.value && !specApproved.value && !isSpecSynced.value ? 'mdi-circle-medium' : null,
    statusColor: 'warning'
  },
  {
    value: 'connect',
    label: '3. Connect GitHub',
    done: !!githubRepo.value,
    doneIcon: githubRepo.value ? 'mdi-checkbox-marked-outline' : 'mdi-checkbox-blank-outline',
    statusIcon: null,
    statusColor: null
  },
  {
    value: 'push',
    label: '4. Push Spec',
    done: isSpecSynced.value,
    doneIcon: isSpecSynced.value ? 'mdi-checkbox-marked-outline' : 'mdi-checkbox-blank-outline',
    statusIcon: isSpecSynced.value ? 'mdi-check-circle' : null,
    statusColor: 'success'
  }
])

// Methods
const navigateToIssue = (issue) => {
  emit('navigateToDomainView', issue)
}

const updateBoundedContext = async (updatedValues, updateNote) => {
  const newBoundedContexts = workflow.value.boundedContexts?.map((item) => {
    if (item.id !== props.boundedContext.id) {
      return item
    }
    return { ...item, ...updatedValues }
  })

  return await store.dispatch('workflow/updateWorkflow', {
    projectId: workflow.value.projectId,
    input: {
      id: workflow.value.id,
      expectedVersion: workflow.value.version,
      boundedContexts: newBoundedContexts,
      updateNote: {
        action: updateNote,
        target: ''
      }
    }
  })
}

const listRepoBranches = async (login, owner, repo, nextPageUrl) => {
  const response = await gitHubApi.listRepoBranches(login, owner, repo, nextPageUrl)

  if (response.success) {
    const currentBranches = response.resData?.branches || []
    const pageUrl = response.resData?.nextPageUrl

    branches.value = nextPageUrl ? [...branches.value, ...currentBranches] : currentBranches

    if (pageUrl) {
      await listRepoBranches(login, owner, repo, pageUrl)
    }
  } else {
    store.commit('snackbar/showMessage', {
      content: response.errorText,
      timeout: 3000,
      color: 'red'
    }, { root: true })
  }
}

const fetchSpecFromGithub = async () => {
  if (!githubAccountLogin.value || !selectedRepo.value || !specFilePath.value) {
    githubFileContent.value = null
    return
  }

  try {
    const branchName = typeof githubBranch.value === 'string'
      ? githubBranch.value
      : (githubBranch.value?.name || 'main')

    const response = await gitHubApi.fetchGitHubRepoFileContent(
      githubAccountLogin.value,
      selectedRepo.value.owner.login,
      selectedRepo.value.name,
      branchName,
      [{ path: specFilePath.value, type: 'blob' }]
    )

    if (response.success && response.resData?.length > 0) {
      githubFileContent.value = response.resData[0].content
    } else {
      githubFileContent.value = null
    }
  } catch {
    githubFileContent.value = null
  }
}

const linkRepo = async () => {
  if (!selectedAccount.value || !selectedRepo.value) {
    return
  }

  isLoading.value = true
  loadingText.value = 'Linking repository...'

  const login = selectedAccount.value.login
  const repo = selectedRepo.value
  const branch = pendingBranch.value || 'main'

  githubAccountLogin.value = login
  githubRepo.value = repo.full_name
  githubBranch.value = branch

  await updateBoundedContext({
    githubAccountLogin: login,
    githubRepo: repo.full_name,
    githubBranch: branch,
    specFilePath: specFilePath.value
  }, 'linked repository')

  await listRepoBranches(login, repo.owner.login, repo.name)
  await fetchSpecFromGithub()

  isLoading.value = false
}

const unlinkRepo = async () => {
  githubAccountLogin.value = ''
  githubRepo.value = ''
  githubBranch.value = null
  selectedRepo.value = null
  selectedAccount.value = null
  branches.value = []
  githubFileContent.value = null

  await updateBoundedContext({
    githubAccountLogin: null,
    githubRepo: null,
    githubBranch: null
  }, 'unlinked repository')
}

const onRepoCreated = (newRepo) => {
  selectedRepo.value = newRepo
  pendingBranch.value = 'main'
  linkRepo()
}

const handleBranchChange = async (branch) => {
  githubBranch.value = branch
  await updateBoundedContext({ githubBranch: branch }, 'updated branch')
  await fetchSpecFromGithub()
}

const saveFilePath = async () => {
  if (specFilePath.value && specFilePath.value !== props.boundedContext.specFilePath) {
    await updateBoundedContext({ specFilePath: specFilePath.value }, 'updated spec file path')
    if (githubRepo.value) {
      await fetchSpecFromGithub()
    }
  }
}

const pushSpecToGithub = async () => {
  if (!selectedRepo.value || !githubAccountLogin.value) {
    store.commit('snackbar/showMessage', {
      content: 'Connect a GitHub repository first.',
      timeout: 3000,
      color: 'red'
    }, { root: true })
    return
  }

  isPushing.value = true
  try {
    const spec = currentExportJson.value
    const branchName = typeof githubBranch.value === 'string'
      ? githubBranch.value
      : (githubBranch.value?.name || 'main')

    const updates = [{
      type: githubFileContent.value !== null ? 'modified' : 'added',
      path: specFilePath.value,
      content: spec
    }]

    const response = await gitHubApi.commitGitHubRepoFiles(
      githubAccountLogin.value,
      selectedRepo.value.owner.login,
      selectedRepo.value.name,
      branchName,
      updates,
      'chore: update specification'
    )

    if (response.success) {
      store.commit('snackbar/showMessage', {
        content: 'Specification pushed to GitHub.',
        timeout: 3000,
        color: 'green'
      }, { root: true })
      await fetchSpecFromGithub()
    } else {
      store.commit('snackbar/showMessage', {
        content: response.errorText,
        timeout: 3000,
        color: 'red'
      }, { root: true })
    }
  } finally {
    isPushing.value = false
  }
}

// Initialization
onMounted(async () => {
  if (githubAccountLogin.value && githubRepo.value) {
    isLoading.value = true
    loadingText.value = 'Loading GitHub connection...'

    const response = await gitHubApi.listGitHubRepos(githubAccountLogin.value)
    if (response.success) {
      const repos = response.resData?.repos || []
      selectedRepo.value = repos.find((r) => r.full_name === githubRepo.value)

      if (selectedRepo.value) {
        await listRepoBranches(
          githubAccountLogin.value,
          selectedRepo.value.owner.login,
          selectedRepo.value.name
        )
        await fetchSpecFromGithub()
      } else {
        store.commit('snackbar/showMessage', {
          content: `Repository ${githubRepo.value} is not accessible. It may have been deleted or you may not have access.`,
          timeout: 5000,
          color: 'warning'
        }, { root: true })
      }
    }

    isLoading.value = false
  }
})

// Reset approval when spec changes (domain model was edited)
watch(currentExportJson, () => {
  specApproved.value = false
})

// Watch for external changes to the bounded context GitHub fields
watch(() => props.boundedContext.githubRepo, (newRepo) => {
  if (newRepo !== githubRepo.value) {
    githubRepo.value = newRepo || ''
  }
})

watch(() => props.boundedContext.githubBranch, (newBranch) => {
  if (newBranch && newBranch !== githubBranch.value) {
    githubBranch.value = newBranch
  }
})
</script>

<style scoped>
.step-item {
  padding: 4px 12px !important;
  min-height: 32px !important;
  cursor: pointer;
}

.step-item .v-list-item__prepend {
  width: 28px;
  min-width: 28px;
}

.validation-list {
  max-height: 200px;
  overflow-y: auto;
}

.validation-item {
  min-height: 32px !important;
  cursor: pointer;
  transition: background-color 0.2s ease;
  align-items: flex-start;
}

.validation-item:hover {
  background-color: rgba(0, 0, 0, 0.04);
}

.validation-item :deep(.v-list-item-title) {
  white-space: normal;
  overflow: visible;
  text-overflow: clip;
  -webkit-line-clamp: unset;
  line-height: 1.35;
  padding-block: 4px;
}

.validation-item :deep(.v-list-item__prepend),
.validation-item :deep(.v-list-item__append) {
  align-self: flex-start;
  padding-top: 4px;
}

.repo-name-ellipsis {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 300px;
}

.approve-btn {
  padding: 1px 8px !important;
  min-width: 40px !important;
  height: auto !important;
  background: #4f535909;
  border: 1px solid var(--dark-1);
  border-radius: 12px !important;
  box-shadow: 0 0 1px 0 #00000026;
  font-size: 12px;
  line-height: 14px;
  color: #4f5359;
  text-transform: none;
  letter-spacing: normal;
}

</style>
